// frontend/src/contexts/CookieConsentContext.tsx
//
// Source of truth for cookie/analytics consent. Persists the choice to
// localStorage and exposes it to both the self-hosted analytics beacon
// (utils/analytics.ts) and the Vercel <Analytics /> component (App.tsx).
//
// Default = NO consent (analytics off) until the user affirmatively accepts.
// "Essential" (auth tokens) is always on and is not gated here.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'pc_cookie_consent';
const CONSENT_VERSION = 1; // bump to re-prompt everyone if categories change

export interface CookieConsent {
  version: number;
  analytics: boolean;
  decidedAt: string; // ISO timestamp of the choice
}

// Module-level snapshot so non-React code (analytics.ts) can read consent
// synchronously without the React tree.
let consentSnapshot: CookieConsent | null = readStored();

function readStored(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed?.version !== CONSENT_VERSION) return null; // stale -> re-prompt
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(c: CookieConsent | null): void {
  try {
    if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — consent stays in-memory only */
  }
}

/**
 * Synchronous consent check for non-React modules (e.g. analytics.ts).
 * Returns true only if the user has affirmatively allowed analytics.
 */
export function getAnalyticsConsent(): boolean {
  return consentSnapshot?.analytics === true;
}

interface CookieConsentContextValue {
  consent: CookieConsent | null;   // null = not yet decided -> show banner
  hasDecided: boolean;
  analyticsAllowed: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  setAnalytics: (allowed: boolean) => void;
  manageOpen: boolean;
  openManage: () => void;
  closeManage: () => void;
}

const Ctx = createContext<CookieConsentContextValue | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readStored());
  const [manageOpen, setManageOpen] = useState(false);

  // Keep the module-level snapshot in sync for non-React readers.
  useEffect(() => { consentSnapshot = consent; }, [consent]);

  const commit = useCallback((analytics: boolean) => {
    const next: CookieConsent = {
      version: CONSENT_VERSION,
      analytics,
      decidedAt: new Date().toISOString(),
    };
    consentSnapshot = next;
    writeStored(next);
    setConsent(next);
    // NOTE (future): also POST `next` to a backend cookie_consents record so
    // consent is demonstrable server-side (GDPR). localStorage is the v1 store.
  }, []);

  const value: CookieConsentContextValue = {
    consent,
    hasDecided: consent !== null,
    analyticsAllowed: consent?.analytics === true,
    acceptAll: () => { commit(true); setManageOpen(false); },
    rejectNonEssential: () => { commit(false); setManageOpen(false); },
    setAnalytics: (allowed: boolean) => commit(allowed),
    manageOpen,
    openManage: () => setManageOpen(true),
    closeManage: () => setManageOpen(false),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
